import { publicSupabase } from '../supabase';

export interface CreateBedPaymentCaseParams {
  /** Soumission pilote (form BED duo). */
  formSubmissionId: string;
  /** Token d'accès crew-scoped présent dans l'URL (?token=). */
  bedAccessToken: string;
}

/** Dossier de paiement BED renvoyé par le socle Victor (create_bed_payment_case). */
export interface BedPaymentCaseData {
  payment_case_id: string;
  bed_token: string;
  order_id: string;
  product_code: string | null;
  amount_cents: number;
  expires_at: string;
  reused?: boolean;
  amount_updated?: boolean;
  revoked_tokens?: number;
}

export type CreateBedPaymentCaseResult =
  | { status: 'ok'; data: BedPaymentCaseData }
  /** 00296 : solde nul ou négatif, aucun dossier à créer, le bulletin se finalise sans. */
  | { status: 'nothing_due'; soldeCents: number | null }
  | { status: 'no_paid_bpi_parent' }
  | { status: 'error'; code?: string };

/**
 * Crée le dossier de paiement BED via la RPC anon
 * `create_bed_payment_case_from_submission` (migration 00230). Cette RPC est un
 * simple WIRING : elle valide le token d'accès puis DÉLÈGUE tout le calcul
 * financier (BPI parent, solde, bed_token, timeline) au socle
 * `create_bed_payment_case` (00130/00144). Aucun montant n'est transmis côté
 * client — le solde est recalculé serveur-side.
 *
 * @returns statut discriminé : `ok` (dossier créé/réutilisé + token de paiement),
 * `nothing_due` (00296 : rien à payer, pas de dossier, finaliser sans),
 * `no_paid_bpi_parent` (l'équipage n'a pas de BPI payé résolvable → message
 * dédié), `error` (ne throw jamais).
 */
export async function createBedPaymentCase(
  params: CreateBedPaymentCaseParams
): Promise<CreateBedPaymentCaseResult> {
  try {
    const { data, error } = await publicSupabase.rpc('create_bed_payment_case_from_submission', {
      p_form_submission_id: params.formSubmissionId,
      p_bed_access_token: params.bedAccessToken,
    });

    if (error) {
      if (error.message?.includes('no_paid_bpi_parent')) {
        return { status: 'no_paid_bpi_parent' };
      }
      console.error('[BED-PAYMENT] Erreur création payment_case BED:', error);
      return { status: 'error', code: error.message };
    }

    const nothingDue = data as { nothing_due?: boolean; solde_cents?: number | null } | null;
    if (nothingDue?.nothing_due) {
      return { status: 'nothing_due', soldeCents: nothingDue.solde_cents ?? null };
    }

    const result = data as BedPaymentCaseData | null;
    if (!result?.payment_case_id) {
      console.error('[BED-PAYMENT] RPC sans payment_case_id retourné');
      return { status: 'error' };
    }

    return { status: 'ok', data: result };
  } catch (error) {
    console.error('[BED-PAYMENT] Exception création payment_case BED:', error);
    return { status: 'error' };
  }
}
